"use client";

/**
 * Requirements Summary:
 * Class schedule slots support multiple assigned coaches while keeping a
 * primary instructor_id schema fallback for older API readers. Time slot
 * deletion uses an inline confirmation popover to avoid accidental removals.
 */

import React from 'react';
import Button from '@/components/ui/button/Button';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { ConfirmPopover } from '@/components/ui/popover';
import SelectField from '@/components/form/fields/SelectField';
import TextField from '@/components/form/fields/TextField';
import MultiSelectField from '@/components/form/fields/MultiSelectField';
import { createLogger } from '@/utils/logger/devLogger';
import {
  getPrimaryClassScheduleInstructorId,
  normalizeClassScheduleInstructorIds,
} from '@/utils/classes/classScheduleInstructors';
import { formatInstructorComboboxLabel } from '@/utils/formatters/nameUtils';
import { WEEKDAY_NAMES } from '@/utils/constants/weekdays';

const logger = createLogger('ScheduleSelector');

export interface ClassScheduleSlot {
  day_of_week: number; // 0 = Sunday, 1 = Monday, etc.
  start_time: string;  // "16:00" format
  end_time: string;    // "17:00" format
  instructor_id?: string;
  instructor_ids?: string[];
  location_id?: string;
  /**
   * Optional per-row date window (YYYY-MM-DD, inclusive). A slot whose
   * effective_date equals its end_date is a one-off ADDED class date (#3354)
   * — it meets exactly once. The class edit form pulls those out of the
   * weekly slot list (see `partitionScheduleSlots`), so this editor only ever
   * renders weekly slots and can ignore both fields.
   */
  effective_date?: string | null;
  end_date?: string | null;
  /**
   * Read-only, computed by GET /api/classes/[id] for session-based classes
   * (#2119): the exact dates this slot meets, and the dates in the pattern
   * that are closed (per-class blocked date or gym-wide closing). YYYY-MM-DD.
   * `meeting_dates_truncated` is true when the session window produced an
   * implausible number of dates (likely a wrong end date) and the lists were
   * capped. The schedule editor ignores all three; the detail page renders them.
   */
  meeting_dates?: string[];
  skipped_dates?: string[];
  meeting_dates_truncated?: boolean;
}

export interface SlotConflict {
  message: string;
  /** Additional messages (e.g., external schedule conflicts) shown as a list */
  additionalMessages?: string[];
}

export interface ScheduleSelectorProps {
  label?: string;
  scheduleSlots: ClassScheduleSlot[];
  onChange: (slots: ClassScheduleSlot[]) => void;
  instructors?: Array<{ id: string; auth_user: { first_name: string; last_name: string; gender?: string | null } }>;
  locations?: Array<{ id: string; name: string }>;
  showLocationField?: boolean;
  showInstructorField?: boolean;
  defaultInstructorName?: string;
  error?: string;
  hint?: string;
  maxSlots?: number;
  slotConflicts?: (SlotConflict | null)[]; // Conflict messages per slot index
}

const ScheduleSelector: React.FC<ScheduleSelectorProps> = ({
  label = "Class Schedule",
  scheduleSlots,
  onChange,
  instructors = [],
  locations = [],
  showLocationField = false,
  showInstructorField = true,
  defaultInstructorName,
  error,
  hint,
  maxSlots = 7,
  slotConflicts = []
}) => {

  const addScheduleSlot = () => {
    if (scheduleSlots.length >= maxSlots) return;
    
    const newSlot: ClassScheduleSlot = {
      day_of_week: 1, // Monday
      start_time: '16:00',
      end_time: '17:00',
      instructor_id: '',
      location_id: ''
    };
    
    onChange([...scheduleSlots, newSlot]);
    logger.info('Added new schedule slot');
  };

  const removeScheduleSlot = (index: number) => {
    const updatedSlots = scheduleSlots.filter((_, i) => i !== index);
    onChange(updatedSlots);
    logger.info(`Removed schedule slot at index ${index}`);
  };

  // Helper function to add one hour to a time string (HH:MM format)
  const addOneHour = (timeString: string): string => {
    const [hours, minutes] = timeString.split(':').map(Number);
    let newHours = hours + 1;
    
    // Handle day rollover (if start time is 23:00 or later, cap at 23:59)
    if (newHours >= 24) {
      newHours = 23;
      const newMinutes = 59;
      return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
    }
    
    return `${newHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const updateScheduleSlot = (index: number, field: keyof ClassScheduleSlot, value: any) => {
    const updatedSlots = scheduleSlots.map((slot, i) => {
      if (i === index) {
        const updatedSlot = { ...slot, [field]: value };

        if (field === 'instructor_ids') {
          const instructorIds = normalizeClassScheduleInstructorIds({
            instructor_ids: Array.isArray(value) ? value : [],
          });
          updatedSlot.instructor_ids = instructorIds;
          updatedSlot.instructor_id = getPrimaryClassScheduleInstructorId({ instructor_ids: instructorIds }) || '';
        }
        
        // Auto-adjust end time when start time changes
        if (field === 'start_time' && value) {
          updatedSlot.end_time = addOneHour(value);
        }
        
        return updatedSlot;
      }
      return slot;
    });
    onChange(updatedSlots);
  };

  // Better layout: use 2 rows instead of cramming everything in one row
  // Row 1: Day, Start Time, End Time (3 cols)
  // Row 2: Instructor, Location (2 cols, wider)
  const shouldUseMultiRow = showInstructorField || showLocationField;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
          </label>
          {hint && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
          )}
        </div>
        
        {scheduleSlots.length < maxSlots && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addScheduleSlot}
            startIcon={<GoogleMaterialIcon name="add" />}
          >
            Add Time Slot
          </Button>
        )}
      </div>

      {scheduleSlots.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <GoogleMaterialIcon name="schedule" size="lg" className="text-gray-400 mb-2" />
          <p className="text-gray-500 dark:text-gray-400 mb-4">No schedule slots defined</p>
          <Button
            type="button"
            variant="outline"
            onClick={addScheduleSlot}
            startIcon={<GoogleMaterialIcon name="add" />}
          >
            Add First Time Slot
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {scheduleSlots.map((slot, index) => (
            <div key={index} className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Time Slot {index + 1}
                </h4>
                <ConfirmPopover
                  message={`Remove time slot ${index + 1}? This will delete the day, time, and assigned coaches for this slot.`}
                  confirmLabel="Remove"
                  position="bottom"
                  onConfirm={() => removeScheduleSlot(index)}
                  trigger={
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      startIcon={<GoogleMaterialIcon name="delete" />}
                    >
                      Remove
                    </Button>
                  }
                />
              </div>
              
              {/* Schedule Fields - Better 2-row layout */}
              <div className="space-y-4">
                {/* Row 1: Day, Start Time, End Time */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Day Selection */}
                  <SelectField
                    id={`day_of_week_${index}`}
                    name={`day_of_week_${index}`}
                    label="Day"
                    value={slot.day_of_week.toString()}
                    onChange={(value) => updateScheduleSlot(index, 'day_of_week', Number(value))}
                    options={WEEKDAY_NAMES.map((day, dayIndex) => ({
                      value: dayIndex.toString(),
                      label: day
                    }))}
                    required
                  />
                  
                  {/* Start Time */}
                  <TextField
                    id={`start_time_${index}`}
                    name={`start_time_${index}`}
                    label="Start Time"
                    type="time"
                    value={slot.start_time}
                    onChange={(e) => updateScheduleSlot(index, 'start_time', e.target.value)}
                    required
                  />
                  
                  {/* End Time */}
                  <TextField
                    id={`end_time_${index}`}
                    name={`end_time_${index}`}
                    label="End Time"
                    type="time"
                    value={slot.end_time}
                    onChange={(e) => updateScheduleSlot(index, 'end_time', e.target.value)}
                    required
                  />
                </div>

                {/* Row 2: Instructor and Location (if shown) */}
                {(showInstructorField || showLocationField) && (
                  <div className={`grid grid-cols-1 ${(showInstructorField && showLocationField) ? 'md:grid-cols-2' : ''} gap-4`}>
                    {/* Instructor Selection */}
                    {showInstructorField && (
                      <div className="w-full">
                        <MultiSelectField
                          id={`instructor_id_${index}`}
                          name={`instructor_id_${index}`}
                          label="Assign Coach(es) for this time slot"
                          value={normalizeClassScheduleInstructorIds(slot)}
                          onChange={(value) => updateScheduleSlot(index, 'instructor_ids', value)}
                          items={instructors.map(instructor => ({
                            id: instructor.id,
                            label: formatInstructorComboboxLabel(instructor)
                          }))}
                          itemKey="id"
                          textKey="label"
                          placeholder={defaultInstructorName ? `Same as lead (${defaultInstructorName})` : 'Select coach'}
                          canClear={true}
                          showSelectedItems={true}
                        />
                        {defaultInstructorName && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Parents will see this coach on the enrollment page for this day.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Location Selection */}
                    {showLocationField && (
                      <SelectField
                        id={`location_id_${index}`}
                        name={`location_id_${index}`}
                        label="Location"
                        value={slot.location_id || ''}
                        onChange={(value) => updateScheduleSlot(index, 'location_id', value)}
                        options={[
                          { value: '', label: 'Select location' },
                          ...locations.map(location => ({
                            value: location.id,
                            label: location.name
                          }))
                        ]}
                        placeholder="Select location"
                      />
                    )}
                  </div>
                )}

                {/* Conflict Warning - Inside time slot card */}
                {slotConflicts[index] && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mt-4">
                    <GoogleMaterialIcon
                      name="warning"
                      size="sm"
                      className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                        Schedule Conflict Detected
                      </p>
                      {slotConflicts[index]!.message && (
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          {slotConflicts[index]!.message}
                        </p>
                      )}
                      {slotConflicts[index]!.additionalMessages && slotConflicts[index]!.additionalMessages!.length > 0 && (
                        <ul className="text-xs text-amber-700 dark:text-amber-300 mt-1 space-y-0.5">
                          {slotConflicts[index]!.additionalMessages!.map((msg, msgIdx) => (
                            <li key={msgIdx}>{msg}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {error && (
        <p className="mt-2 text-xs text-error-500">{error}</p>
      )}
    </div>
  );
};

export default ScheduleSelector;
